import React, { useState, useRef, useCallback } from 'react';
import { FloatingPortal } from '@floating-ui/react';
import api from '../../services/api';
import { useAnchoredDropdown } from './useAnchoredDropdown';
import './PareigybeSelect.scss';

export interface PareigybeRef {
  id: number;
  name: string;
  tipas_name?: string | null;
  grupe_name?: string | null;
}

interface Props {
  value: PareigybeRef[];
  onChange: (v: PareigybeRef[]) => void;
  multiple?: boolean;
  placeholder?: string;
  /** Show the derived type next to each result (display only). */
  showTipas?: boolean;
  className?: string;
}

/**
 * Canonical "Pareigybė" (position) selector. Type-ahead against
 * /api/taxonomy/positions/search, which is synonym-aware: typing a synonym
 * surfaces the canonical positions in that group. This is the single
 * user-facing taxonomy selector — field / type / synonym group / company
 * activity are all derived from the chosen position, never selected here.
 */
const PareigybeSelect: React.FC<Props> = ({ value, onChange, multiple = false, placeholder, showTipas = true, className }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<PareigybeRef[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { refs, floatingStyles, getReferenceProps, getFloatingProps } = useAnchoredDropdown(
    open,
    (o) => { setOpen(o); if (!o) setSearch(''); },
  );

  const runSearch = useCallback((q: string) => {
    if (timer.current) clearTimeout(timer.current);
    // 1-char queries are noisy; otherwise search (empty returns the top
    // positions so the control browses like a dropdown).
    if (q.trim().length === 1) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/taxonomy/positions/search', { params: { q, limit: 50 } });
        setResults(res.data?.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, []);

  const handleInput = (q: string) => {
    setSearch(q);
    setOpen(true);
    runSearch(q);
  };

  const handleFocus = () => {
    setOpen(true);
    // Show the position list immediately (top page) so it reads as a select.
    if (results.length === 0) runSearch(search);
  };

  const selectedIds = new Set(value.map(v => v.id));

  const handleSelect = (p: PareigybeRef) => {
    if (multiple) {
      if (selectedIds.has(p.id)) return;
      onChange([...value, p]);
      setSearch('');
      setResults([]);
    } else {
      onChange([p]);
      setSearch('');
      setResults([]);
      setOpen(false);
    }
  };

  const handleRemove = (id: number) => onChange(value.filter(v => v.id !== id));

  return (
    <div className={`pareigybe-select ${className || ''}`} ref={refs.setReference} {...getReferenceProps()}>
      {value.length > 0 && (
        <div className="pareigybe-select__tags">
          {value.map(v => (
            <span key={v.id} className="pareigybe-select__tag">
              {v.name}
              <button type="button" onClick={() => handleRemove(v.id)} aria-label="Pašalinti">×</button>
            </span>
          ))}
        </div>
      )}
      {(multiple || value.length === 0) && (
        <input
          type="text"
          className="pareigybe-select__input"
          value={search}
          placeholder={placeholder || 'Įveskite pareigybę...'}
          onChange={e => handleInput(e.target.value)}
          onFocus={handleFocus}
        />
      )}
      {open && (loading || results.length > 0 || search.trim().length >= 1) && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="pareigybe-select__dropdown"
          >
            {loading && <div className="pareigybe-select__loading">Ieškoma...</div>}
            {!loading && results.length === 0 && search.trim().length >= 1 && (
              <div className="pareigybe-select__empty">Nieko nerasta</div>
            )}
            {!loading && results.map(p => (
              <div
                key={p.id}
                className={`pareigybe-select__option ${selectedIds.has(p.id) ? 'is-selected' : ''}`}
                onMouseDown={e => { e.preventDefault(); handleSelect(p); }}
              >
                <span className="pareigybe-select__option-name">{p.name}</span>
                {showTipas && p.tipas_name && <span className="pareigybe-select__option-tipas">{p.tipas_name}</span>}
              </div>
            ))}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default PareigybeSelect;
